/**
 * The scene graph's z-order, established once so nothing downstream has to
 * restructure it.
 *
 * `docs/05_ART_DIRECTION.md` makes this a hard visual rule rather than a
 * convenience: the world layer is where chaos lives (terrain, and from Phase 5
 * eruption/flood particles), and the civilisation layer — ownership, borders,
 * buildings — must read clearly *on top of* whatever the world is doing. Wiring
 * `units` and `effects` now, while they are empty, costs nothing and means
 * Phases 5 and 6 add content instead of reordering the tree.
 */

import { Container } from "pixi.js";
import type { Application } from "pixi.js";

export interface RenderLayers {
  /** Camera-transformed parent. The camera writes to this container only. */
  readonly world: Container;
  /** Biome fills. Bottom of the stack. */
  readonly terrain: Container;
  /** Ownership, borders, buildings, capture meters, selection affordances. */
  readonly civ: Container;
  /** Unit sprites. Empty until Phase 6. */
  readonly units: Container;
  /** Particles and world events. Empty until Phase 5. */
  readonly effects: Container;
}

export function createLayers(app: Application): RenderLayers {
  const world = new Container();
  const terrain = new Container();
  const civ = new Container();
  const units = new Container();
  const effects = new Container();

  world.label = "world";
  terrain.label = "terrain";
  civ.label = "civ";
  units.label = "units";
  effects.label = "effects";

  // Add order is z-order; nothing sets zIndex, so no sorting pass runs.
  world.addChild(terrain);
  world.addChild(civ);
  world.addChild(units);
  world.addChild(effects);

  // Effects sit on top, matching the stack named in Stage 1.2 of the roadmap.
  // Worth flagging: docs/05 also says particles should read as *environmental*,
  // underneath ownership. Those two only conflict if a Phase 5 effect is drawn
  // opaque — the fix then is the effect's alpha, not this ordering, which the
  // roadmap fixes explicitly.
  app.stage.addChild(world);

  return { world, terrain, civ, units, effects };
}
